import {
  nextTick,
  onMounted,
  onUnmounted,
  watch,
  type ComponentPublicInstance,
  type Ref,
} from 'vue';

function resolveElement(target: unknown): HTMLElement | null {
  if (!target) {
    return null;
  }
  if (target instanceof Element) {
    return target as HTMLElement;
  }
  if (typeof target === 'object' && '$el' in target) {
    const el = (target as ComponentPublicInstance).$el;
    if (el instanceof Element) {
      return el as HTMLElement;
    }
  }
  return null;
}

/**
 * Publica en :root la altura real de un banner fijo para que el layout
 * (header, tabs, etc.) pueda desplazarse sin tapar contenido con fuente grande.
 */
export function useFixedBannerHeight(
  elementRef: Ref<unknown>,
  cssVariable: string,
  isActive: () => boolean,
): void {
  let resizeObserver: ResizeObserver | null = null;
  let observedElement: HTMLElement | null = null;

  const update = (): void => {
    const element = resolveElement(elementRef.value);
    const height = isActive() && element ? element.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty(cssVariable, `${Math.ceil(height)}px`);
  };

  const unobserveCurrent = (): void => {
    if (resizeObserver && observedElement) {
      resizeObserver.unobserve(observedElement);
      observedElement = null;
    }
  };

  const observeCurrent = (): void => {
    if (!resizeObserver) {
      return;
    }
    unobserveCurrent();
    const element = resolveElement(elementRef.value);
    if (!element) {
      update();
      return;
    }
    resizeObserver.observe(element);
    observedElement = element;
    update();
  };

  onMounted(() => {
    resizeObserver = new ResizeObserver(update);
    void nextTick(() => {
      observeCurrent();
    });
  });

  onUnmounted(() => {
    unobserveCurrent();
    resizeObserver?.disconnect();
    resizeObserver = null;
    document.documentElement.style.setProperty(cssVariable, '0px');
  });

  watch(
    () => isActive(),
    () => {
      update();
    },
  );

  watch(elementRef, () => {
    observeCurrent();
  });
}
